import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, User, Menu, X } from 'lucide-react';
import { NAV_LINKS } from '../mock';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2 group">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="transition-transform group-hover:rotate-6">
      <path d="M6 22 L18 6 L26 12 L14 28 Z" fill="#00e676" stroke="#00e676" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M14 28 L22 20" stroke="#062516" strokeWidth="2"/>
    </svg>
    <span className="text-xl font-extrabold tracking-tight text-white whitespace-nowrap">NEXUS FX <span className="text-[#00e676]">PRO</span></span>
  </Link>
);

const NavDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button className="nav-link flex items-center gap-1 text-sm font-medium text-gray-300 py-6">
        {label}
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`}/>
      </button>
      {open && (
        <div className="absolute top-full left-0 min-w-[220px] bg-[#0a1210] border border-[#1a2823] rounded-xl shadow-2xl py-2 z-50">
          {items.map(it => (
            <a key={it} href="#" className="block px-4 py-2.5 text-sm text-gray-300 hover:text-[#00e676] hover:bg-[#0f1a17] transition-colors">
              {it}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hide header on dashboard and admin
  if (location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/admin')) return null;

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? 'bg-[#050807]/85 backdrop-blur-xl border-b border-[#1a2823]' : 'bg-transparent'}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-auto">
        <Logo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map(nl => <NavDropdown key={nl.label} label={nl.label} items={nl.items}/>)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/login" className="hidden sm:flex w-9 h-9 rounded-full border border-[#1a2823] items-center justify-center text-gray-300 hover:text-[#00e676] hover:border-[#00e676] transition">
            <User size={16}/>
          </Link>
          <Link to="/login" className="btn-outline-green px-3 sm:px-5 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">Sign In</Link>
          <button className="lg:hidden text-white p-1" onClick={() => setMobileOpen(v => !v)} aria-label="menu">
            {mobileOpen ? <X size={22}/> : <Menu size={22}/>}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="lg:hidden bg-[#0a1210] border-t border-[#1a2823] px-6 py-4 space-y-3">
          {NAV_LINKS.map(nl => (
            <details key={nl.label} className="text-gray-300">
              <summary className="cursor-pointer py-2 font-medium">{nl.label}</summary>
              <div className="pl-4 space-y-1">
                {nl.items.map(it => <a key={it} href="#" className="block py-1 text-sm text-gray-400">{it}</a>)}
              </div>
            </details>
          ))}
        </div>
      )}
    </header>
  );
};

export default Header;
